import React, { useMemo } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { cellFlags, columnsFor, formatAmount, isIntlFormat } from './beneficiaryValidationHelpers';

const MatchStatusChip = ({ value }) =>
  value ? <span className={`bv-chip ${/not matched/i.test(value) ? '' : 'bv-chip-neutral'}`}>{value}</span> : null;

// Rows come back as the whole set, so this grid scrolls (it virtualises rows) instead of paging.
const ValidationGrid = ({ rows, bankFormat, totals, highlightAmount, isFiltered }) => {
  const intl = isIntlFormat(bankFormat);

  const columnDefs = useMemo(
    () =>
      columnsFor(bankFormat).map((col, index) => {
        const def = {
          headerName: col.headerName,
          field: col.field,
          width: col.width,
          suppressMovable: true,
          sortable: true,
          cellClass: (params) => {
            const classes = [];
            if (col.isAmount) classes.push('bv-amount');
            if (params.node?.rowPinned) classes.push('bv-total-row');
            else if (cellFlags(params.data, highlightAmount).includes(col.field)) classes.push('bv-cell-mismatch');
            return classes;
          },
        };
        if (col.isAmount) {
          def.type = 'rightAligned';
          def.valueFormatter = (p) => formatAmount(p.value);
        }
        if (col.isStatus) {
          def.cellRenderer = MatchStatusChip;
        }
        if (index === 0) {
          // "No match" chip sits in the first column, ahead of the value.
          def.cellRenderer = (params) => (
            <span>
              {params.data?.isUnitNotFound && !params.node?.rowPinned ? <span className="bv-chip">no match</span> : null}
              {params.value}
            </span>
          );
        }
        return def;
      }),
    [bankFormat, highlightAmount],
  );

  const pinnedBottomRowData = useMemo(() => {
    if (intl || !totals) return [];
    const first = columnsFor(bankFormat)[0].field;
    return [
      {
        [first]: isFiltered ? 'Total (all rows)' : 'Total',
        systemAmount: totals.totalSystemAmount,
        bankAmount: totals.totalBankAmount,
      },
    ];
  }, [intl, totals, bankFormat, isFiltered]);

  return (
    <div className="ag-theme-quartz" style={{ height: 500 }}>
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        pinnedBottomRowData={pinnedBottomRowData}
        suppressRowClickSelection={true}
        rowClassRules={{ 'bv-row-notfound': (params) => !!params.data?.isUnitNotFound }}
      />
    </div>
  );
};

export default ValidationGrid;
